/**
 * Pricing data and utilities - single source of truth for all pricing information.
 */
import type { ReactNode } from 'react';

import { Handshake, Network, Zap } from 'lucide-react';

import { DEMO_URL } from '@/common/constants';

// Currency configuration
const CURRENCY = 'USD';
const LOCALE = 'en-US';

const currencyFormatter = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: CURRENCY,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatPrice(price: number): string {
  return currencyFormatter.format(price);
}

// Types
export type PricingFeature = string | { text: string; tooltip: string };

type PricingTierData = {
  id: string;
  name: string;
  price: number | null; // null = custom pricing
  priceMax?: number; // for range pricing
  priceLabel: string;
  priceHint?: string;
  description: string;
  includesPreviousTier?: string;
  features: PricingFeature[];
  cta:
    | { text: string; href: string; external?: boolean }
    | { text: string; action: 'waitlist' | 'contact' };
  comingSoon?: boolean;
  popular?: boolean;
};

// UI type: same as data but with formatted price string and optional icon
export type PricingTier = Omit<PricingTierData, 'id' | 'price' | 'priceMax'> & {
  icon?: ReactNode;
  price?: string; // formatted display price
};

// Icon mapping by tier id
const TIER_ICONS: Record<string, ReactNode> = {
  product: <Zap className="h-5 w-5" />,
  salesPartner: <Handshake className="h-5 w-5" />,
  omniAgent: <Network className="h-5 w-5" />,
};

/**
 * Raw pricing tier data - the single source of truth.
 */
const PRICING_TIERS_DATA: PricingTierData[] = [
  {
    id: 'product',
    name: 'Product',
    price: null,
    priceLabel: 'starting from 1 year commitment',
    description: 'AI-powered commerce on your website, live in 72 hours',
    features: [
      '10,000 messages included',
      'Full Product and Website Integration',
      '1 AI Agent (Website Co-Pilot)',
      'Analytics dashboard',
      'Email support',
    ],
    cta: {
      text: 'Join the Waitlist',
      action: 'waitlist',
    },
  },
  {
    id: 'salesPartner',
    name: 'Partner',
    price: null,
    priceLabel: 'margin from closed deals',
    priceHint: 'Tiered rates - ideal for CEOs who want a revenue-aligned partner.',
    description: 'Bazak sells for you - pay only when deals close',
    includesPreviousTier: 'Bazak Product',
    features: [
      {
        text: 'Revenue-share model',
        tooltip: '% of Bazak AI-attributed closed deals',
      },
      'Full Product and Website Integration',
      '1 AI Agent (Website Co-Pilot)',
      'Analytics dashboard',
      'Dedicated support',
    ],
    cta: {
      text: 'Join the Waitlist',
      action: 'waitlist',
    },
  },
  {
    id: 'omniAgent',
    name: 'Operating System',
    price: null,
    priceLabel: 'starting from 1 year commitment',
    description: 'Sell across every AI channel - ChatGPT, Claude, MCP, and beyond',
    includesPreviousTier: 'Bazak Product',
    features: [
      {
        text: 'Full AI Ecosystem Access',
        tooltip:
          'Connect to ChatGPT, Claude, MCP marketplaces, and vertical AI platforms via Bazak Bridge',
      },
      {
        text: 'Your brand, selling natively inside the AI ecosystem',
        tooltip: 'Users can browse, compare, and buy directly within AI assistants - no redirects',
      },
      {
        text: 'Advanced Analytics',
        tooltip: 'Sentiment analysis, Topics analysis, Conversation insights',
      },
      {
        text: 'Agentic Commerce Protocols',
        tooltip:
          'Design Partnership for selected early adopters: Support for AP2, ACP, and UCP - let AI agents complete purchases on behalf of users',
      },
      'SLAs & Success manager (CSM)',
    ],
    cta: {
      text: 'Schedule a Demo',
      href: DEMO_URL,
      external: true,
    },
    popular: true,
  },
];

/**
 * Get formatted display price for a tier.
 */
function getDisplayPrice(tier: PricingTierData): string {
  if (tier.price === null) {
    return 'Custom';
  }
  if (tier.priceMax) {
    return `${formatPrice(tier.price)} - ${formatPrice(tier.priceMax)}`;
  }
  return formatPrice(tier.price);
}

/**
 * Pricing tiers with icons - for use in UI components.
 */
export const PRICING_TIERS: PricingTier[] = PRICING_TIERS_DATA.map(tier => ({
  name: tier.name,
  icon: TIER_ICONS[tier.id],
  price: getDisplayPrice(tier),
  priceLabel: tier.priceLabel,
  priceHint: tier.priceHint,
  description: tier.description,
  includesPreviousTier: tier.includesPreviousTier,
  features: tier.features,
  cta: tier.cta,
  comingSoon: tier.comingSoon,
  popular: tier.popular,
}));

/**
 * Get price range for JSON-LD structured data.
 */
export function getPriceRange() {
  const prices = PRICING_TIERS_DATA.flatMap(tier => {
    if (tier.price === null) {
      return [];
    }
    return tier.priceMax ? [tier.price, tier.priceMax] : [tier.price];
  });

  return {
    currency: CURRENCY,
    lowPrice: Math.min(...prices),
    highPrice: Math.max(...prices),
    offerCount: PRICING_TIERS_DATA.length,
  };
}
